
import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import { FaSync, FaChevronLeft, FaChevronRight, FaExpand } from "react-icons/fa";
import TimeFilter from "../components/TimeFilter";
import InfoTooltip from "../components/InfoTooltip";
import TenantTabs from "../components/TenantTabs";
import LoadingOverlay from "../components/LoadingOverlay";
import ExpandedChartModal from "../components/ExpandedChartModal";
import { fetchSupportTickets } from "../services/supportService";
import { useTimeFilter } from "../context/TimeFilterContext";

/**
 * @file Support.js
 * @description Support dashboard for managing tickets, SLAs, and customer feedback.
 */
export default function Support() {
    const { timeFilter, setTimeFilter, customRange, handleCustomRangeChange } = useTimeFilter();
    const [activeTab, setActiveTab] = useState("estrel");
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [data, setData] = useState(null);
    const [expandedChart, setExpandedChart] = useState(null);

    const handleExpandChart = (title, options, series, type) => {
        setExpandedChart({ title, options, series, type });
    };

    // Pagination
    const [ticketPage, setTicketPage] = useState(1);
    const itemsPerPage = 5;

    const loadData = (force = false) => {
        // Guard Clause
        if (timeFilter === 'custom' && (!customRange?.start || !customRange?.end)) return;
        if (timeFilter === 'custom' && new Date(customRange.start) > new Date(customRange.end)) return;

        setLoading(true);
        // Updated signature: timePeriod, tenantCategory, customRange, forceRefresh
        fetchSupportTickets(timeFilter, activeTab, customRange, force).then((res) => {
            // Sanitize Chart Data
            if (res && res.charts) {
                if (res.charts.categoryDistribution) {
                    res.charts.categoryDistribution = res.charts.categoryDistribution.map(v => Number(v) || 0);
                }
                if (res.charts.resolutionTrend && res.charts.resolutionTrend.data) {
                    res.charts.resolutionTrend.data = res.charts.resolutionTrend.data.map(v => Number(v) || 0);
                }
            }
            setData(res);
            setLastUpdated(new Date());

            // Delay to allow charts to render
            setTimeout(() => {
                setLoading(false);
            }, 600);
        });
    };

    useEffect(() => {
        if (timeFilter === 'custom' && (!customRange?.start || !customRange?.end)) return;
        if (timeFilter === 'custom' && new Date(customRange.start) > new Date(customRange.end)) return;
        loadData();
    }, [timeFilter, activeTab, customRange]);

    if (!data) {
        return (
            <div className="min-vh-100 position-relative bg-light">
                <LoadingOverlay loading={true} message="Loading Support Data..." />
            </div>
        );
    }

    // API filtered data, no need for local filtering
    const filteredTickets = data.tickets || [];

    const lineOptions = {
        chart: { type: "line", toolbar: { show: false }, animations: { enabled: false } },
        stroke: { curve: "smooth", width: 2 },
        colors: ["#3b82f6"],
        xaxis: { categories: data.charts.resolutionTrend.categories },
        markers: { size: 4, hover: { size: 6 } }, // Ensure points are visible
    };

    const donutOptions = {
        chart: { type: "donut", animations: { enabled: false } },
        labels: data.rawCategories ? data.rawCategories.map(c => c.category) : ["Category"],
        colors: ["#3b82f6", "#10b981", "#8b5cf6", "#f59e0b", "#64748b"],
        legend: { position: "right" },
        plotOptions: { pie: { donut: { size: "70%" } } }
    };

    return (
        <div className="p-4 bg-light min-vh-100 position-relative">
            <LoadingOverlay loading={loading} />
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="fw-bold mb-1">Support & Feedback</h4>
                    <p className="text-muted small mb-0">Manage support tickets and customer feedback.</p>
                </div>
                <div className="d-flex align-items-center gap-3">
                    <TimeFilter
                        value={timeFilter}
                        onChange={setTimeFilter}
                        startDate={customRange.start}
                        endDate={customRange.end}
                        onCustomRangeChange={handleCustomRangeChange}
                    />
                    <div className="text-end d-none d-md-block ms-2">
                        <div className="text-muted" style={{ fontSize: "0.75rem" }}>Last Update</div>
                        <div className="fw-bold small">{lastUpdated ? lastUpdated.toLocaleTimeString() : "-"}</div>
                    </div>
                    <button className="btn btn-white border shadow-sm btn-sm px-3" onClick={() => loadData(true)} disabled={loading}>
                        <FaSync className={loading ? "fa-spin me-2" : "me-2"} /> Refresh
                    </button>
                </div>
            </div>

            {/* KPI ROW */}
            <div className="row g-3 mb-4">
                {[
                    { label: "Open Tickets", val: data.kpis.openTickets, tip: "Tickets currently awaiting resolution." },
                    { label: "SLA Breaches", val: data.kpis.slaBreaches, tip: "Tickets exceeding response time limits." },
                    { label: "Avg Resolution", val: data.kpis.avgResolution, tip: "Average time to close a ticket." },
                    { label: "CSAT Score", val: data.kpis.csatScore, tip: "Customer Satisfaction score (out of 5)." },
                ].map((k, i) => (
                    <div key={i} className="col-xl-3 col-md-6">
                        <div className="card border-0 shadow-sm p-3 h-100">
                            <div className="text-muted small fw-bold text-uppercase">
                                {k.label} <InfoTooltip text={k.tip} />
                            </div>
                            <h3 className="fw-bold mb-0">{k.val}</h3>
                        </div>
                    </div>
                ))}
            </div>

            {/* CHARTS */}
            <div className="row g-4 mb-4">
                <div className="col-12 col-lg-6">
                    <div className="card border-0 shadow-sm p-4 h-100">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="fw-bold mb-0 text-uppercase">Tickets by Category</h6>
                            <button className="btn btn-sm btn-link text-secondary p-0" onClick={() => handleExpandChart("Tickets by Category", donutOptions, data.charts.categoryDistribution, "donut")}>
                                <FaExpand />
                            </button>
                        </div>
                        {data.charts.categoryDistribution.some(v => v > 0) ? (
                            <Chart options={donutOptions} series={data.charts.categoryDistribution} type="donut" height={250} />
                        ) : (
                            <div className="d-flex align-items-center justify-content-center h-100 text-muted small bg-light rounded" style={{ minHeight: "250px" }}>
                                No data to show
                            </div>
                        )}
                    </div>
                </div>
                <div className="col-12 col-lg-6">
                    <div className="card border-0 shadow-sm p-4 h-100">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="fw-bold mb-0 text-uppercase">Resolution Time Trend (30 Days)</h6>
                            <button className="btn btn-sm btn-link text-secondary p-0" onClick={() => handleExpandChart("Resolution Time Trend", lineOptions, [{ name: "Avg Time (hrs)", data: data.charts.resolutionTrend.data }], "line")}>
                                <FaExpand />
                            </button>
                        </div>
                        {data.charts.resolutionTrend.data.some(v => v > 0) ? (
                            <Chart options={lineOptions} series={[{ name: "Avg Time (hrs)", data: data.charts.resolutionTrend.data }]} type="line" height={250} />
                        ) : (
                            <div className="d-flex align-items-center justify-content-center h-100 text-muted small bg-light rounded" style={{ minHeight: "250px" }}>
                                No data to show
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* TENANT TABS */}
            <TenantTabs activeTab={activeTab} onTabChange={setActiveTab} />

            {/* TICKET TABLE */}
            <div className="card border-0 shadow-sm rounded-3 mt-4">
                <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                    <h6 className="fw-bold mb-0">Recent Tickets ({activeTab})</h6>
                    <div className="d-flex align-items-center gap-1 bg-light rounded px-2 py-1">
                        <button className="btn btn-sm btn-link text-dark p-0 text-decoration-none" onClick={() => setTicketPage(prev => Math.max(prev - 1, 1))} disabled={ticketPage === 1} style={{ fontSize: '1.2rem', lineHeight: '1' }}>
                            <FaChevronLeft size={12} />
                        </button>
                        <span className="small text-muted mx-2" style={{ fontSize: '0.8rem', minWidth: '40px', textAlign: 'center' }}>
                            {ticketPage} / {Math.ceil(filteredTickets.length / itemsPerPage) || 1}
                        </span>
                        <button className="btn btn-sm btn-link text-dark p-0 text-decoration-none" onClick={() => setTicketPage(prev => Math.min(prev + 1, Math.ceil(filteredTickets.length / itemsPerPage)))} disabled={ticketPage >= Math.ceil(filteredTickets.length / itemsPerPage)} style={{ fontSize: '1.2rem', lineHeight: '1' }}>
                            <FaChevronRight size={12} />
                        </button>
                    </div>
                </div>
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th className="ps-4 py-3">Ticket ID</th>
                                <th>Tenant</th>
                                <th>Category</th>
                                <th>Issue</th>
                                <th>Priority</th>
                                <th>Status</th>
                                <th className="pe-4 text-end">Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTickets.length > 0 ? (
                                filteredTickets.slice((ticketPage - 1) * itemsPerPage, ticketPage * itemsPerPage).map((t, i) => (
                                    <tr key={i}>
                                        <td className="ps-4 font-monospace small">{t.id}</td>
                                        <td className="text-dark">{t.tenant}</td>
                                        <td>{t.category}</td>
                                        <td>{t.issue}</td>
                                        <td>
                                            <span className={`badge ${t.priority === "Critical" ? "bg-danger" : t.priority === "High" ? "bg-warning text-dark" : "bg-info-subtle text-info"} `}>
                                                {t.priority}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`badge ${t.status === "Open" ? "bg-danger-subtle text-danger" : t.status === "Closed" ? "bg-success-subtle text-success" : "bg-primary-subtle text-primary"} `}>
                                                {t.status}
                                            </span>
                                        </td>
                                        <td className="pe-4 text-end text-muted small">{t.created}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="text-center py-5 text-muted">
                                        No tickets found for {activeTab} in this time range.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="mt-4">
                <h6 className="fw-bold mb-3">Feedback Sentiment Analysis</h6>
                <div className="row g-3">
                    <div className="col-md-4">
                        <div className="card border-0 shadow-sm p-3 border-start border-success border-4">
                            <div className="small text-muted">Positive</div>
                            <div className="fw-bold fs-4">{data.sentiment.positive}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card border-0 shadow-sm p-3 border-start border-secondary border-4">
                            <div className="small text-muted">Neutral</div>
                            <div className="fw-bold fs-4">{data.sentiment.neutral}</div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card border-0 shadow-sm p-3 border-start border-danger border-4">
                            <div className="small text-muted">Negative</div>
                            <div className="fw-bold fs-4">{data.sentiment.negative}</div>
                        </div>
                    </div>
                </div>
            </div>

            <ExpandedChartModal
                show={!!expandedChart}
                onClose={() => setExpandedChart(null)}
                {...expandedChart}
            />
        </div>
    );
}
